import type { ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'
import { getActiveEventSlug } from '../services/eventService'
import { LoadingState } from '../components/ui/States'

/**
 * Gate for the attendee experience.
 * - waits for the session check
 * - if auth is unavailable (offline/demo), allows through (open mode)
 * - if auth is available but not signed in, redirects to the event's branded
 *   sign-in when the event slug is known (consistent with ScopedApp), else the
 *   generic landing directory. This avoids bouncing an event-scoped attendee to
 *   the generic "enter your email" screen.
 */
export default function RequireAttendee({ children }: { children: ReactNode }) {
  const { ready, authAvailable, isAuthenticated } = useAttendeeAuth()
  const { slug: routeSlug } = useParams()
  const slug = routeSlug || getActiveEventSlug()

  if (!ready) return <LoadingState label="Loading your event…" />
  if (authAvailable && !isAuthenticated) {
    return <Navigate to={slug ? `/e/${slug}/signin` : '/'} replace />
  }
  return <>{children}</>
}
